













































import { inject } from 'inversify-props';
import { Vue, Watch, Prop, Component } from 'vue-property-decorator';
import IAuthService from '../../../services/IAuthService';
import { InputValidation } from '../../../helpers/inputValidation';
import { Organization } from '@models';
import { cloneDeep, debounce } from 'lodash';

@Component
export default class OrganizationSelector extends Vue {
  name = 'organization-selector';

  @Prop(String) id!: string;
  @Prop({ type: Object as () => Organization }) parent!: Organization;
  @Prop(Boolean) isAdvertiserSearch!: boolean;
  @Prop(Function) onError: any;
  @Prop({ type: Boolean, default: false }) required!: boolean;
  @Prop({
    type: Array as () => ((value: any) => true | 'Value is missing')[],
    default: (): ((value: any) => true | 'Value is missing')[] => []
  }) rules!: ((value: any) => true | 'Value is missing')[];

  @Prop({ type: Object as () => Organization }) value: Organization;

  searchText: string = "";
  pageNumber: number = 1;
  pageSize: number = 50;
  options: Organization[] = [];
  selected: Organization[] = [];
  isLoading: boolean = false;
  noMoreOptions: boolean = false;
  optionsMax: number = 1000;
  @inject() private authService: IAuthService;
  input = InputValidation;

  get internalRules() {
    const rules: ((value: any) => true | 'Value is missing')[] = cloneDeep(this.rules) || [];

    if (this.required) {
      rules.push(this.input.required)
    }
    return rules;
  }

  onSelectionMenuClosed(value, id) {
    this.$refs.inputComponent.validate(this.internalRules);
  }

  updateValue(value: Organization) {
    this.$emit('input', value)
  }

  async created() {
    await this.loadOrganizations();
  }

  reset() {
    this.options = [];
  }

  @Watch("parent")
  async loadDecendants(): Promise<void> {
    if (this.parent?.id) {
      this.reset();
      await this.loadOrganizations();
    }
  }

  sort() {
    this.options = this.options.sort(
      (a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }

  organizationFilterTaglimitText(count) {
    return `and ${count} other items`
  }

  asyncFindOrganizationsDebounced = debounce((query, pageNumber = 1) => {
    this.asyncFindOrganizations(query, pageNumber);
  }, 300);

  asyncFindOrganizations(query, pageNumber = 1) {
    this.isLoading = true;

    if (pageNumber && pageNumber > 1)
      this.pageNumber = pageNumber;
    else {
      this.noMoreOptions = false;
      this.pageNumber = 1;
    }

    this.searchText = query;

    this.fetchOrganizations().then(response => {
      if (this.pageNumber === 1) {
        this.options = response;
        this.sort();
      }
      else {
        this.options = this.options.concat(response);
        this.sort();
      }

      this.noMoreOptions = response.length < 1;
      this.isLoading = false;
    });
  }

  async fetchOrganizations(): Promise<Organization[]> {
    let url = '/api/organization/getuserorganizationsV3';
    const params: { name: string, value: any }[] = [];

    if (this.pageNumber && this.pageNumber > 0) {
      params.push({ name: 'pageNumber', value: this.pageNumber });
    }

    if (this.pageSize && this.pageSize > 0) {
      params.push({ name: 'pageSize', value: this.pageSize });
    }

    if (this.searchText && this.searchText.trim() !== "") {
      params.push({ name: 'searchText', value: this.searchText.trim() });
    }

    if (this.parent?.id) {
      params.push({ name: 'parentId', value: this.parent.id });
    }

    if (this.isAdvertiserSearch) {
      params.push({ name: 'getAdvertisers', value: true });
    }
    else {
      params.push({ name: 'getOrganisations', value: true });
    }

    if (params.length > 0)
      url += '?' + params[0].name + '=' + params[0].value;
    for (let i = 1; i < params.length; i++) {
      url += '&' + params[i].name + '=' + params[i].value;
    }

    let result = [];
    try {
      const response = await this.authService.get(url);
      result = await response.data;
    }
    catch (error) {
      if (this.onError && typeof this.onError === 'function') {
        this.onError(error.response);
      }
    }

    return result.data;
  }

  async loadOrganizations(): Promise<void> {
    this.isLoading = true;
    this.options = await this.fetchOrganizations();
    this.sort();
    this.isLoading = false;
  }
}
